import React from "react";
import { motion, AnimatePresence } from "framer-motion";

interface UnoCallAnimationProps {
  active: boolean;
  playerName?: string;
  isSelf?: boolean;
  position?: React.CSSProperties;
}

/**
 * Dramatic UNO call animation - shows when a player reaches 1 card or calls UNO
 * Mobile game style: bold, impactful, short duration (300ms max)
 */
export const UnoCallAnimation: React.FC<UnoCallAnimationProps> = ({
  active,
  playerName,
  isSelf = false,
  position,
}) => {
  const defaultPosition: React.CSSProperties = isSelf
    ? { top: "50%", left: "50%", transform: "translate(-50%, -50%)" }
    : { top: "20%", left: "50%", transform: "translateX(-50%)" };

  return (
    <AnimatePresence>
      {active && (
        <>
          {/* Main UNO badge with dramatic scale + fade */}
          <motion.div
            initial={{ scale: 0, opacity: 0, rotate: -10 }}
            animate={{
              scale: [0, 1.3, 1],
              opacity: [0, 1, 1],
              rotate: [-10, 2, 0],
            }}
            exit={{ scale: 0.8, opacity: 0, rotate: 5 }}
            transition={{
              duration: 0.3,
              times: [0, 0.6, 1],
              ease: [0.34, 1.56, 0.64, 1], // Overshoot bounce
            }}
            style={{
              position: "fixed",
              ...defaultPosition,
              ...(position || {}),
              zIndex: 7000,
              pointerEvents: "none",
            }}
          >
            <motion.div
              animate={{
                scale: [1, 1.05, 1],
              }}
              transition={{
                duration: 0.3,
                repeat: 1,
                ease: "easeInOut",
              }}
              style={{
                background: isSelf
                  ? `linear-gradient(135deg, var(--card-green, #2ecc71) 0%, color-mix(in srgb, var(--card-green, #2ecc71) 80%, black) 100%)`
                  : `linear-gradient(135deg, var(--card-yellow, #f39c12) 0%, color-mix(in srgb, var(--card-yellow, #f39c12) 80%, black) 100%)`,
                color: "white",
                padding: "20px 40px",
                borderRadius: "20px",
                fontSize: "64px",
                fontWeight: "900",
                textShadow: "0 4px 20px rgba(0,0,0,0.5)",
                boxShadow: isSelf
                  ? `0 12px 60px color-mix(in srgb, var(--card-green, #2ecc71) 80%, transparent), 0 0 40px color-mix(in srgb, var(--card-green, #2ecc71) 40%, transparent)`
                  : `0 12px 60px color-mix(in srgb, var(--card-yellow, #f39c12) 80%, transparent), 0 0 40px color-mix(in srgb, var(--card-yellow, #f39c12) 40%, transparent)`,
                letterSpacing: "4px",
                border: "4px solid rgba(255, 255, 255, 0.3)",
                backdropFilter: "blur(10px)",
              }}
            >
              UNO!
            </motion.div>
          </motion.div>

          {/* Particle burst effect */}
          {isSelf && (
            <>
              {[...Array(8)].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{
                    scale: 0,
                    opacity: 1,
                    x: 0,
                    y: 0,
                  }}
                  animate={{
                    scale: [0, 1, 0],
                    opacity: [1, 1, 0],
                    x: Math.cos((i / 8) * Math.PI * 2) * 100,
                    y: Math.sin((i / 8) * Math.PI * 2) * 100,
                  }}
                  transition={{
                    duration: 0.3,
                    delay: 0.05,
                    ease: "easeOut",
                  }}
                  style={{
                    position: "fixed",
                    top: "50%",
                    left: "50%",
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    background: "#2ecc71",
                    boxShadow: "0 0 20px #2ecc71",
                    zIndex: 6999,
                    pointerEvents: "none",
                  }}
                />
              ))}
            </>
          )}
        </>
      )}
    </AnimatePresence>
  );
};
